// Frontend/src/components/admin/CellGroupManager.jsx

import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useCampuses } from '../../contexts/CampusContext';
import { useCellMinistries } from '../../contexts/CellMinistryContext';
import { useLeaders } from '../../contexts/LeaderContext';
import CellMinistryService from '../../services/cellGroupsServices';
import CellForm from './forms/CellForm';
import Button from './ui/Button';
import ConfirmDialog from './ui/ConfirmDialog';
import DataTable from './ui/DataTable';
import Modal from './ui/Modal';

const CellGroupManager = () => {
  const { cellGroups, loading, error, setCellGroups } = useCellMinistries();
  const { leaders } = useLeaders();
  const { campuses } = useCampuses();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [currentMinistry, setCurrentMinistry] = useState(null);
  const [selectedCampusId, setSelectedCampusId] = useState(null);
  const [processing, setProcessing] = useState(false);
  
  // Modern UI enhancements
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCampusFilter, setSelectedCampusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'table'
  const [showStats, setShowStats] = useState(true);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const searchInputRef = useRef(null);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case 'k':
            e.preventDefault();
            searchInputRef.current?.focus();
            break;
          case 'n':
            e.preventDefault();
            if (campuses.length > 0) {
              setCurrentMinistry(null);
              setSelectedCampusId(campuses[0].id);
              setIsModalOpen(true);
            }
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [campuses]);

  const groupedCellGroups = useMemo(() => {
    const groups = {};

    campuses.forEach(campus => {
      groups[campus.id] = {
        campus,
        cellGroups: []
      };
    });

    cellGroups.forEach(cellGroup => {
      const campusId = cellGroup.campusId || cellGroup.campus_id;
      if (groups[campusId]) {
        groups[campusId].cellGroups.push(cellGroup);
      }
    });

    return groups;
  }, [cellGroups, campuses]);

  // Enhanced filtering and sorting
  const filteredAndSortedGroups = useMemo(() => {
    let filtered = { ...groupedCellGroups };

    // Apply search filter
    if (searchTerm) {
      Object.keys(filtered).forEach(campusId => {
        filtered[campusId].cellGroups = filtered[campusId].cellGroups.filter(group =>
          group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          group.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          group.location?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      });
    }

    // Apply campus filter
    if (selectedCampusFilter !== 'all') {
      const campusFilter = {};
      campusFilter[selectedCampusFilter] = filtered[selectedCampusFilter];
      filtered = campusFilter;
    }

    // Sort cell groups within each campus
    Object.keys(filtered).forEach(campusId => {
      filtered[campusId].cellGroups.sort((a, b) => {
        const aVal = a[sortBy] || '';
        const bVal = b[sortBy] || '';
        
        if (sortOrder === 'asc') {
          return aVal.localeCompare(bVal);
        } else {
          return bVal.localeCompare(aVal);
        }
      });
    });

    return filtered;
  }, [groupedCellGroups, searchTerm, selectedCampusFilter, sortBy, sortOrder]);

  // Statistics
  const stats = useMemo(() => {
    const totalGroups = cellGroups.length;
    const groupsWithLeaders = cellGroups.filter(group => {
      const hasLeader = leaders.some(leader => 
        leader.leaderType.includes('cell') && 
        leader.cellgroupId.includes(group.id)
      );
      return hasLeader;
    }).length;
    
    const activeCampuses = Object.keys(groupedCellGroups).filter(
      campusId => groupedCellGroups[campusId].cellGroups.length > 0
    ).length;

    return {
      totalGroups,
      groupsWithLeaders,
      groupsWithoutLeaders: totalGroups - groupsWithLeaders,
      activeCampuses,
      averageGroupsPerCampus: activeCampuses > 0 ? Math.round(totalGroups / activeCampuses) : 0
    };
  }, [cellGroups, leaders, groupedCellGroups]);

  const columns = [
    {
      field: 'name',
      header: 'Name',
      width: '30%',
      className: 'font-semibold'
    },
    {
      field: 'leader',
      header: 'Leader',
      width: '25%',
      render: (row) => {
        const matchingLeaders = leaders.filter(
          (leader) => {
            let isAssignedToGroup = false;
            if (leader.leaderType.includes('cell')) {
              isAssignedToGroup = leader.cellgroupId.map((leaderid) => (
                leaderid === row.id
            ))
              .includes(true);
            }
            return isAssignedToGroup;
          }
        );

        if (matchingLeaders.length === 0) {
          return <span className="text-gray-400 italic">No leader assigned</span>;
        }
        return (
          <div className="space-y-1">
            {matchingLeaders.map((leader, index) => (
              <div key={leader.id || index} className="text-sm">
                {leader.name}
              </div>
            ))}
          </div>
        );
      }
    },
    {
      field: 'meeting_day',
      header: 'Meeting Day',
      width: '20%',
      className: 'hidden sm:table-cell',
      render: (row) => row.meeting_day || '—'
    },
    {
      field: 'meeting_time',
      header: 'Time',
      width: '15%',
      className: 'hidden md:table-cell',
      render: (row) => row.meeting_time || '—'
    },
    {
      field: 'actions',
      header: 'Actions',
      width: '10%',
      className: 'text-right',
      render: (row) => (
        <div className="flex flex-col sm:flex-row gap-1 sm:gap-2 sm:justify-end">
          <button
            onClick={() => handleEdit(row)}
            className="btn btn-xs sm:btn-sm btn-outline w-full sm:w-auto hover:scale-105 transition-transform"
            disabled={processing}
          >
            Edit
          </button>
          <button
            onClick={() => handleDeleteClick(row)}
            className="btn btn-xs sm:btn-sm btn-error w-full sm:w-auto hover:scale-105 transition-transform"
            disabled={processing}
          >
            Delete
          </button>
        </div>
      )
    }
  ];

  const handleEdit = (cellGroup) => {
    setCurrentMinistry(cellGroup);
    setSelectedCampusId(cellGroup.campusId || cellGroup.campus_id);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (cellGroup) => {
    setCurrentMinistry(cellGroup);
    setIsDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!currentMinistry) return;

    setProcessing(true);
    try {
      await CellMinistryService.deleteMinistry(currentMinistry.id);
      setCellGroups(cellGroups.filter(item => item.id !== currentMinistry.id));
      toast.success('Cell group deleted successfully');
      setIsDeleteConfirmOpen(false);
      setCurrentMinistry(null);
    } catch (error) {
      toast.error(`Failed to delete cell group: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const handleCreateForCampus = (campusId) => {
    setCurrentMinistry(null);
    setSelectedCampusId(campusId);
    setIsModalOpen(true);
  };

  const handleSubmit = async (formData) => {
    setProcessing(true);
    try {
      const dataWithCampus = {
        ...formData,
        campusId: selectedCampusId
      };

      let updatedMinistries;
      if (currentMinistry) {
        const updated = await CellMinistryService.updateMinistry(currentMinistry.id, dataWithCampus);
        updatedMinistries = cellGroups.map(m =>
          m.id === currentMinistry.id ? updated : m
        );
        toast.success('Cell group updated successfully');
      } else {
        const created = await CellMinistryService.createMinistry(dataWithCampus);
        updatedMinistries = [...cellGroups, created];
        toast.success('Cell group created successfully');
      }

      setCellGroups(updatedMinistries);
      setIsModalOpen(false);
      setCurrentMinistry(null);
      setSelectedCampusId(null);
    } catch (error) {
      toast.error(`Failed to save cell group: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const getCampusName = (campusId) => {
    const campus = campuses.find(c => c.id === campusId);
    return campus ? campus.name : 'Unknown Campus';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex justify-center items-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-blue-200 rounded-full animate-spin"></div>
            <div className="w-16 h-16 border-4 border-blue-500 rounded-full animate-spin absolute top-0 left-0" style={{clipPath: 'polygon(0% 0%, 50% 0%, 50% 100%, 0% 100%)'}}></div>
          </div>
          <p className="text-gray-600 font-medium">Loading cell groups...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header Section with Glass Effect */}
      <div className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-white/20 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 rounded-2xl bg-gradient-to-r from-blue-500 to-purple-600 shadow-lg">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                  Cell Groups
                </h1>
                <p className="text-gray-600 mt-1">Manage and organize your community groups</p>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex items-center space-x-3">
              <div className="hidden md:flex items-center space-x-2 text-xs text-gray-500 bg-gray-100 rounded-lg px-3 py-2">
                <kbd className="kbd kbd-xs">⌘</kbd>
                <span>+</span>
                <kbd className="kbd kbd-xs">K</kbd>
                <span>Search</span>
              </div>
              <div className="hidden md:flex items-center space-x-2 text-xs text-gray-500 bg-gray-100 rounded-lg px-3 py-2">
                <kbd className="kbd kbd-xs">⌘</kbd>
                <span>+</span>
                <kbd className="kbd kbd-xs">N</kbd>
                <span>New</span>
              </div>
              <Button
                onClick={() => handleCreateForCampus(campuses[0]?.id)}
                variant="primary"
                className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105"
                disabled={processing || campuses.length === 0}
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                New Group
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-800 shadow-sm">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-3 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {error.message || 'Failed to load cell groups'}
            </div>
          </div>
        )}

        {/* Statistics Cards */}
        {showStats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Groups</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.totalGroups}</p>
                </div>
                <div className="p-3 rounded-xl bg-blue-100">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">With Leaders</p>
                  <p className="text-3xl font-bold text-green-600">{stats.groupsWithLeaders}</p>
                </div>
                <div className="p-3 rounded-xl bg-green-100">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Need Leaders</p>
                  <p className="text-3xl font-bold text-amber-600">{stats.groupsWithoutLeaders}</p>
                </div>
                <div className="p-3 rounded-xl bg-amber-100">
                  <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Active Campuses</p>
                  <p className="text-3xl font-bold text-purple-600">{stats.activeCampuses}</p>
                </div>
                <div className="p-3 rounded-xl bg-purple-100">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Controls Bar */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-lg mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Search and Filters */}
            <div className="flex flex-col sm:flex-row gap-4 flex-1">
              <div className="relative flex-1 max-w-md">
                <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search cell groups..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white/80 backdrop-blur-sm"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>

              <select
                value={selectedCampusFilter}
                onChange={(e) => setSelectedCampusFilter(e.target.value)}
                className="px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white/80 backdrop-blur-sm"
              >
                <option value="all">All Campuses</option>
                {campuses.map(campus => (
                  <option key={campus.id} value={campus.id}>{campus.name}</option>
                ))}
              </select>
            </div>

            {/* View Controls */}
            <div className="flex items-center space-x-3">
              <div className="flex items-center bg-gray-100 rounded-xl p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg transition-all duration-200 ${
                    viewMode === 'grid' 
                      ? 'bg-white shadow-sm text-blue-600' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  className={`p-2 rounded-lg transition-all duration-200 ${
                    viewMode === 'table' 
                      ? 'bg-white shadow-sm text-blue-600' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                </button>
              </div>

              <button
                onClick={() => setShowStats(!showStats)}
                className={`p-2 rounded-xl transition-all duration-200 ${
                  showStats 
                    ? 'bg-blue-100 text-blue-600' 
                    : 'bg-gray-100 text-gray-600 hover:text-gray-900'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Campus Sections */}
        <div className="space-y-8">
          {Object.entries(filteredAndSortedGroups).map(([campusId, { campus, cellGroups: campusCellGroups }]) => (
            <div key={campusId} className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/20 shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300">
              <div className="bg-gradient-to-r from-gray-50 to-blue-50 px-6 py-4 border-b border-gray-200/50">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                  <div className="flex items-center space-x-4">
                    <div className="p-2 rounded-xl bg-gradient-to-r from-blue-500 to-purple-600">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">{campus.name}</h2>
                      <p className="text-sm text-gray-600">
                        {campusCellGroups.length} cell group{campusCellGroups.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={() => handleCreateForCampus(campus.id)}
                    variant="primary"
                    size="sm"
                    className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 shadow-md hover:shadow-lg transition-all duration-200 hover:scale-105"
                    disabled={processing}
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add Group
                  </Button>
                </div>
              </div>

              <div className="p-6">
                {campusCellGroups.length > 0 ? (
                  viewMode === 'grid' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {campusCellGroups.map((group) => {
                        const groupLeaders = leaders.filter(
                          (leader) => leader.leaderType.includes('cell') && 
                          leader.cellgroupId.includes(group.id)
                        );

                        return (
                          <div key={group.id} className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 hover:scale-105 hover:border-blue-300">
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex-1">
                                <h3 className="font-semibold text-gray-900 text-lg mb-2">{group.name}</h3>
                                <div className="space-y-2">
                                  {groupLeaders.length > 0 ? (
                                    <div className="flex items-center text-sm text-gray-600">
                                      <svg className="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                      </svg>
                                      {groupLeaders.map(leader => leader.name).join(', ')}
                                    </div>
                                  ) : (
                                    <div className="flex items-center text-sm text-amber-600">
                                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                      </svg>
                                      No leader assigned
                                    </div>
                                  )}
                                  
                                  {group.meeting_day && (
                                    <div className="flex items-center text-sm text-gray-600">
                                      <svg className="w-4 h-4 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                      </svg>
                                      {group.meeting_day}
                                      {group.meeting_time && ` at ${group.meeting_time}`}
                                    </div>
                                  )}
                                  
                                  {group.location && (
                                    <div className="flex items-center text-sm text-gray-600">
                                      <svg className="w-4 h-4 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                      </svg>
                                      {group.location}
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              <div className="flex items-center space-x-1 ml-4">
                                <button
                                  onClick={() => handleEdit(group)}
                                  className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200"
                                  disabled={processing}
                                  title="Edit group"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => handleDeleteClick(group)}
                                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200"
                                  disabled={processing}
                                  title="Delete group"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                            
                            {group.description && (
                              <p className="text-sm text-gray-600 mt-3 p-3 bg-gray-50 rounded-lg">
                                {group.description}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="overflow-x-auto rounded-xl border border-gray-200">
                      <DataTable
                        data={campusCellGroups}
                        columns={columns}
                        loading={false}
                        emptyMessage={`No cell groups found for ${campus.name}`}
                        className="min-w-full"
                      />
                    </div>
                  )
                ) : (
                  <div className="text-center py-12">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-100 to-purple-100 rounded-full mb-6">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No cell groups yet</h3>
                    <p className="text-gray-600 mb-6 max-w-md mx-auto">
                      Start building your community by creating the first cell group for {campus.name}.
                    </p>
                    <Button
                      onClick={() => handleCreateForCampus(campus.id)}
                      variant="primary"
                      className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105"
                      disabled={processing}
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Create First Cell Group
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* No Campuses State */}
        {campuses.length === 0 && (
          <div className="text-center py-20">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-gray-100 to-blue-100 rounded-full mb-8">
              <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">No campuses available</h3>
            <p className="text-gray-600 max-w-md mx-auto mb-8">
              You need to create campuses first before you can start managing cell groups. 
              Campuses help organize your cell groups by location.
            </p>
            <div className="flex justify-center">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 max-w-md">
                <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mx-auto mb-4">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-sm text-blue-800 font-medium">
                  Head to the Campus Management section to create your first campus, then return here to add cell groups.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Empty Search Results */}
        {campuses.length > 0 && Object.keys(filteredAndSortedGroups).length === 0 && (
          <div className="text-center py-20">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-6">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No results found</h3>
            <p className="text-gray-600 mb-6">
              Try adjusting your search or filter criteria to find what you're looking for.
            </p>
            <div className="flex justify-center space-x-3">
              <Button
                onClick={() => setSearchTerm('')}
                variant="outline"
                size="sm"
              >
                Clear Search
              </Button>
              <Button
                onClick={() => setSelectedCampusFilter('all')}
                variant="outline"
                size="sm"
              >
                Show All Campuses
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setCurrentMinistry(null);
          setSelectedCampusId(null);
        }}
        title={
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-xl bg-gradient-to-r from-blue-500 to-purple-600">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold">
                {currentMinistry ? 'Edit' : 'Create'} Cell Group
              </h3>
              {selectedCampusId && (
                <p className="text-sm text-gray-600">
                  {getCampusName(selectedCampusId)}
                </p>
              )}
            </div>
          </div>
        }
        size="lg"
        className="mx-2 sm:mx-4"
      >
        <CellForm
    initialValues={currentMinistry || {
      name: '',
      leader: '',
      description: '',
      meetingDay: '',
      meetingTime: '',
      meetingLocation: '',
      location: ''
    }}
    onSubmit={handleSubmit}
    onCancel={() => {
      setIsModalOpen(false);
      setCurrentMinistry(null);
      setSelectedCampusId(null);
    }}
    campuses={campuses}
    showCampusField={!selectedCampusId} // Only show campus field if no specific campus was selected
    defaultCampusId={selectedCampusId} // Pre-select the campus if one was specified
    isSubmitting={processing}
  />
      </Modal>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={isDeleteConfirmOpen}
        onClose={() => {
          setIsDeleteConfirmOpen(false);
          setCurrentMinistry(null);
        }}
        onConfirm={handleDelete}
        title={
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-xl bg-red-100">
              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </div>
            <span className="text-lg font-semibold text-gray-900">Confirm Deletion</span>
          </div>
        }
        message={
          <div className="space-y-3">
            <p className="text-gray-700">
              Are you sure you want to delete <strong>"{currentMinistry?.name}"</strong> cell group?
            </p>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <svg className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-red-800">This action cannot be undone</p>
                  <p className="text-sm text-red-700 mt-1">
                    All data associated with this cell group will be permanently deleted.
                  </p>
                </div>
              </div>
            </div>
          </div>
        }
        confirmText="Delete Group"
        cancelText="Cancel"
        processing={processing}
        confirmColor="danger"
        className="mx-2 sm:mx-4"
      />
    </div>
  );
};

export default CellGroupManager;